import { PersonAppearance, PersonLane } from '@app/modules/persons/appearances/types';
import { TimeRange } from '@mi-front/core';
import { Stream } from '@mi-front/ui';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useState } from 'react';
import { STREAM_UI_CONFIG } from '../../../../../consts';
import { usePersonLaneIdentity } from '../../hooks/usePersonLaneIdentity';
import { applyLaneHover, HoverLaneSetter } from '../../shared/apply-lane-hover';
import { PersonLaneAvatar } from '../../shared/PersonLaneAvatar';
import {
  APPEARANCE_BAR_DIMMED_OPACITY,
  getLaneBarLayout,
  LANE_AVATAR_PX,
} from '../../shared/persons-track-visual-tokens';
import { AppearanceBar } from '../AppearanceBar';

const LANE_HEIGHT = STREAM_UI_CONFIG.personsLaneHeight;

type PersonLaneRowProps = {
  lane: PersonLane;
  range: TimeRange<number>;
  /** Dim bars when another lane is highlighted in the group. */
  dimmed?: boolean;
  /** Outline this lane's bars when it is the active hover target. */
  emphasized?: boolean;
  onHoverLane?: HoverLaneSetter;
};

/**
 * One person's horizontal swim lane in expanded Personas density.
 *
 * Shows that person's face/voice appearance bars on a shared time axis, plus a
 * left overlay (avatar; name expands on row hover). Hovering the row or a bar
 * highlights this person across the whole track and dims the others.
 */
export function PersonLaneRow({
  lane,
  range,
  dimmed = false,
  emphasized = false,
  onHoverLane,
}: PersonLaneRowProps) {
  const { nameNode, isIdentityReady } = usePersonLaneIdentity(lane);
  const [rowHovered, setRowHovered] = useState(false);

  if (!isIdentityReady) {
    return null;
  }

  const isMixed = lane.face.length > 0 && lane.voice.length > 0;

  const barFor = (a: PersonAppearance) => {
    const { height, offsetY } = getLaneBarLayout(a.source, isMixed, LANE_HEIGHT);
    return (
      <AppearanceBar
        key={`${a.source[0]}-${a.from}`}
        lane={lane}
        appearance={a}
        height={height}
        offsetY={offsetY}
        dimmed={dimmed}
        emphasized={emphasized}
        onHoverLane={onHoverLane}
      />
    );
  };

  return (
    <Box
      sx={{
        position: 'relative',
        height: LANE_HEIGHT,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
      onMouseEnter={() => {
        setRowHovered(true);
        if (onHoverLane) {
          applyLaneHover(onHoverLane, lane.laneKey, true);
        }
      }}
      onMouseLeave={() => {
        setRowHovered(false);
        if (onHoverLane) {
          applyLaneHover(onHoverLane, lane.laneKey, false);
        }
      }}
    >
      <Stream
        showAxis={false}
        height={LANE_HEIGHT}
        range={range}
        renderHtml={() => (
          <>
            {lane.face.map(barFor)}
            {lane.voice.map(barFor)}
          </>
        )}
      />
      <Box
        sx={{
          position: 'absolute',
          left: 4,
          top: STREAM_UI_CONFIG.trackContentInsetY,
          height: LANE_HEIGHT - 2 * STREAM_UI_CONFIG.trackContentInsetY,
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 0.5,
          borderRadius: 1,
          bgcolor: (theme) =>
            rowHovered ? alpha(theme.palette.background.paper, 0.92) : 'transparent',
          pointerEvents: 'none',
          maxWidth: rowHovered ? '40%' : 'auto',
          zIndex: 3,
          opacity: dimmed ? APPEARANCE_BAR_DIMMED_OPACITY : 1,
          transition: 'background-color 120ms, opacity 120ms',
        }}
      >
        <PersonLaneAvatar lane={lane} sizePx={LANE_AVATAR_PX} borderWidth={0} dashedWhenUnknown />
        {rowHovered && (
          <Typography variant="caption" noWrap>
            {nameNode}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
